import React from "react";
import { NodeID } from "@hex-insights/app-modules";
import {
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Paragraph,
	Row,
	Section,
	StyleProps,
	toLocalDateString,
	Tooltip,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import {
	Contract,
	ContractInstallment,
	ContractInstallmentDetailQuery,
	Invoice,
	InvoiceStatus,
	Payment,
	PaymentLineItem,
	Student,
	StudentNameAndImageFragment,
	useContractInstallmentDetailQuery,
} from "../../../../Utilities";
import {
	ContractLineItemFormat,
	InvoiceFormat,
	InvoiceLineItemFormat,
	ModelFormat,
	PaymentFormat,
	PaymentLineItemFormat,
	StudentFormat,
} from "../../../Format";
import { PaymentForm } from "../../../Forms";
import { PaymentLineItemFormValues } from "../../../FormUtilities";
import { ContractInstallmentUtilities, ContractUtilities, InvoiceUtilities, ModelUtilities } from "../../../Utilities";
import { PaymentStatusBadge } from "../../_General/PaymentStatus";
import { InvoiceCreateOnContractInstallmentButton } from "../../Invoice";
import { PaymentCreateModalController } from "../../Payment/CreateModal";
import { StudentIcon } from "../../Student";
import styles from "./styles.module.css";

export type ControlledContractInstallmentModalProps = {
	contractInstallmentID: ContractInstallment["id"];
} & Omit<ContractInstallmentModalProps, "isLoading" | "contractInstallment">;

export function ControlledContractInstallmentModal({
	contractInstallmentID,
	...props
}: ControlledContractInstallmentModalProps) {
	const { loading, data, refetch } = useContractInstallmentDetailQuery({ variables: { id: contractInstallmentID } });
	const contractInstallment = data?.contractInstallment;

	return (
		<ContractInstallmentModal
			isLoading={loading}
			contractInstallment={contractInstallment}
			refetchContractInstallment={refetch}
			{...props}
		/>
	);
}

export type ModalContractInstallment = ContractInstallmentDetailQuery["contractInstallment"];

export type ContractInstallmentModalProps = {
	variant: "admin" | "parent";
	isLoading?: boolean;
	contractInstallment: ModalContractInstallment | null | undefined;
	refetchContractInstallment?: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ContractInstallmentModal({
	variant,
	isLoading = false,
	contractInstallment,
	refetchContractInstallment,
	ifRef,
	onClose,
	className,
	style,
}: ContractInstallmentModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Contract Installment</Heading.H2>
			</Modal.Header>
			<Conditional>
				<If condition={isLoading}>
					<Modal.Body>Loading...</Modal.Body>
				</If>
				<If
					condition={
						!!contractInstallment && contractInstallment.invoices.some((e) => e.status === InvoiceStatus.Issued)
					}
				>
					{!!contractInstallment && (
						<ContractInstallmentModalInvoiceContent
							variant={variant}
							contractInstallment={contractInstallment}
							refetchContractInstallment={refetchContractInstallment}
						/>
					)}
				</If>
				<Else>
					{!!contractInstallment && (
						<ContractInstallmentModalComputedContent
							variant={variant}
							contractInstallment={contractInstallment}
							refetchContractInstallment={refetchContractInstallment}
						/>
					)}
				</Else>
			</Conditional>
		</Modal>
	);
}

type ContractInstallmentModalInvoiceContentProps = {
	variant: "admin" | "parent";
	contractInstallment: ModalContractInstallment;
	refetchContractInstallment?: () => void;
};

function ContractInstallmentModalInvoiceContent({
	variant,
	contractInstallment,
	refetchContractInstallment,
}: ContractInstallmentModalInvoiceContentProps) {
	const { invoice, totalAmount, totalPaid, amountDue, paymentStatus, lineItems } = React.useMemo(() => {
		const invoice = contractInstallment.invoices[contractInstallment.invoices.length - 1];
		const totalAmount = InvoiceUtilities.calculateTotalAmount(invoice);
		const totalPaid = InvoiceUtilities.calculateTotalPaid(invoice);
		const amountDue = Math.max(totalAmount - totalPaid, 0);
		const paymentStatus = InvoiceUtilities.getPaymentStatusFromValues(amountDue, invoice.dueDate);

		const lineItems = invoice.invoiceLineItems.map((e) => ({
			id: e.id,
			name: InvoiceLineItemFormat.name(e),
			amount: e.amount,
		}));

		return {
			invoice,
			totalAmount,
			totalPaid,
			amountDue,
			paymentStatus,
			lineItems,
		};
	}, [contractInstallment]);

	return (
		<ContractInstallmentModalContentDisplay
			variant={variant}
			contractInstallmentID={contractInstallment.id}
			invoiceID={invoice.id}
			contract={contractInstallment.contract}
			totalAmount={totalAmount}
			totalPaid={totalPaid}
			amountDue={amountDue}
			paymentStatus={paymentStatus}
			invoiceIssueDate={invoice.issueDate}
			paymentDueDate={invoice.dueDate}
			lineItems={lineItems}
			paymentLineItems={invoice.paymentLineItems}
			onPaymentCreateSuccess={refetchContractInstallment}
		/>
	);
}

type ContractInstallmentModalComputedContentProps = {
	variant: "admin" | "parent";
	contractInstallment: ModalContractInstallment;
	refetchContractInstallment?: () => void;
};

function ContractInstallmentModalComputedContent({
	variant,
	contractInstallment,
	refetchContractInstallment,
}: ContractInstallmentModalComputedContentProps) {
	const { installmentAmount, totalPaid, amountDue, paymentStatus, lineItems, paymentLineItems } = React.useMemo(() => {
		const installmentLineItemAmounts = ContractUtilities.calculateContractInstallmentLineItemPrices(
			contractInstallment.contract,
		);

		const installmentAmount = ContractUtilities.calculateContractInstallmentAmounts(
			contractInstallment.contract,
			installmentLineItemAmounts,
		)[contractInstallment.id];
		const totalPaid = ContractInstallmentUtilities.calculateTotalPaid(contractInstallment);
		const amountDue = Math.max(installmentAmount - totalPaid, 0);

		const paymentStatus = ContractInstallmentUtilities.getPaymentStatus(
			contractInstallment,
			installmentAmount,
			totalPaid,
		);

		const lineItemAmounts = installmentLineItemAmounts[contractInstallment.id];
		const lineItems = contractInstallment.contract.contractLineItems.map((e) => ({
			id: e.id,
			name: ContractLineItemFormat.name(e),
			amount: lineItemAmounts[e.id] ?? 0,
		}));

		const paymentLineItems: ContractInstallmentModalContentDisplayProps["paymentLineItems"] = [];

		return { installmentAmount, totalPaid, amountDue, paymentStatus, lineItems, paymentLineItems };
	}, [contractInstallment]);

	return (
		<ContractInstallmentModalContentDisplay
			variant={variant}
			contractInstallmentID={contractInstallment.id}
			invoiceID={null}
			contract={contractInstallment.contract}
			totalAmount={installmentAmount}
			totalPaid={totalPaid}
			amountDue={amountDue}
			paymentStatus={paymentStatus}
			invoiceIssueDate={null}
			paymentDueDate={contractInstallment.dueDate}
			lineItems={lineItems}
			paymentLineItems={paymentLineItems}
			onPaymentCreateSuccess={refetchContractInstallment}
		/>
	);
}

const lineItemTableColumns = ["index", "name", "amount"];
const lineItemTableColumnWidths = {
	index: "3rem",
};

const paymentTableColumns = ["index", "issueDate", "paymentMethod", "amount"];
const paymentTableColumnsWithExhange = [...paymentTableColumns, "exchange"];
const paymentTableColumnWidths = {
	index: "3rem",
	exchange: "2.5rem",
};

type ContractInstallmentModalContentDisplayProps = {
	variant: "admin" | "parent";
	contractInstallmentID: ContractInstallment["id"];
	invoiceID: Invoice["id"] | null;
	contract: Pick<Contract, "id"> & { student: Pick<Student, "id"> & StudentNameAndImageFragment };
	totalAmount: number;
	totalPaid: number;
	amountDue: number;
	paymentStatus: ModelUtilities.PaymentStatus;
	invoiceIssueDate: string | null;
	paymentDueDate: string;
	lineItems: {
		id: NodeID;
		name: string;
		amount: number;
	}[];
	paymentLineItems: (Pick<PaymentLineItem, "id" | "amount" | "localAmount"> & {
		payment: Pick<Payment, "issueDate" | "paymentMethod" | "isInLocalCurrency" | "localCurrency">;
	})[];
	onPaymentCreateSuccess?: () => void;
};

function ContractInstallmentModalContentDisplay({
	variant,
	contractInstallmentID,
	invoiceID,
	contract,
	totalAmount,
	totalPaid,
	amountDue,
	paymentStatus,
	invoiceIssueDate,
	paymentDueDate,
	lineItems,
	paymentLineItems,
	onPaymentCreateSuccess,
}: ContractInstallmentModalContentDisplayProps) {
	const hasInvoice = !!invoiceID;

	const createFormStateArgs = React.useMemo<PaymentForm.ControlledCreateProps["formStateArgs"]>(() => {
		return {
			initialFormValues: {
				issueDate: toLocalDateString(new Date()),
				paymentLineItems: [
					{
						...PaymentLineItemFormValues.blankItemInPayment,
						displayOrder: 0,
						invoiceID,
						amount: amountDue,
						localAmount: 0,
					},
				],
			},
		};
	}, [invoiceID, amountDue]);

	const anyPaymentsInLocalCurrency = paymentLineItems.some((e) => e.payment.isInLocalCurrency);

	return (
		<React.Fragment>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Section>
						<Section.Body>
							<Row justify="space-between">
								<Grid tracks={2} gap="1rem 3rem">
									<LabeledItem label="Student">
										<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
											<StudentIcon student={contract.student} size={32} />
											{StudentFormat.name(contract.student)}
										</Row>
									</LabeledItem>
									<LabeledItem label="Status">
										<PaymentStatusBadge status={paymentStatus} withIcon />
									</LabeledItem>
									<LabeledItem label="Date Issued">
										<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
											<Icon.CalendarBlank size={14} />{" "}
											{invoiceIssueDate ? InvoiceFormat.Fields.issueDate(invoiceIssueDate) : "N/A"}
										</Row>
									</LabeledItem>
									<LabeledItem label="Date Due">
										<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
											<Icon.CalendarBlank size={14} /> {InvoiceFormat.Fields.dueDate(paymentDueDate)}
										</Row>
									</LabeledItem>
								</Grid>

								<LabeledItem label="Balance due">
									<div className={styles["balance-due"]}>{ModelFormat.money(amountDue)}</div>
								</LabeledItem>
							</Row>
						</Section.Body>
					</Section>

					<Section>
						<Section.Header>
							<Heading.H3 noMargin>Items</Heading.H3>
						</Section.Header>
						<Section.Body>
							<Column justify="spaced-start" verticalSpacing="1rem">
								<Table
									columns={lineItemTableColumns}
									columnWidths={lineItemTableColumnWidths}
									className={styles["items-table"]}
								>
									<Table.Header className={styles["items-table__header"]}>
										<Table.Row>
											<Table.Heading column="index" noResize>
												#
											</Table.Heading>
											<Table.Heading column="name" noResize>
												Item
											</Table.Heading>
											<Table.Heading column="amount" noResize className={styles["items-table__amount-column"]}>
												Amount
											</Table.Heading>
										</Table.Row>
									</Table.Header>
									<Table.Body>
										{lineItems.map((lineItem, i) => (
											<Table.Row key={lineItem.id} className={styles["items-table__row"]}>
												<Table.Cell column="index" numeric>
													{i + 1}
												</Table.Cell>
												<Table.Cell column="name">{lineItem.name}</Table.Cell>
												<Table.Cell column="amount" className={styles["items-table__amount-column"]}>
													{InvoiceLineItemFormat.Fields.amount(lineItem.amount)}
												</Table.Cell>
											</Table.Row>
										))}
									</Table.Body>
								</Table>

								<table className={styles["summary-table"]}>
									<tbody>
										<tr>
											<td className={styles["summary-table__label"]}>Total Due:</td>
											<td className={styles["summary-table__value"]}>{ModelFormat.money(totalAmount)}</td>
										</tr>
										<tr>
											<td className={styles["summary-table__label"]}>Total Paid:</td>
											<td className={styles["summary-table__value"]}>{ModelFormat.money(totalPaid)}</td>
										</tr>
										<tr>
											<td className={styles["summary-table__label"]}>Balance Due:</td>
											<td className={styles["summary-table__value"]}>{ModelFormat.money(amountDue)}</td>
										</tr>
									</tbody>
								</table>
							</Column>
						</Section.Body>
					</Section>

					<Conditional>
						<If condition={hasInvoice}>
							<Section>
								<Section.Header>
									<Heading.H3 noMargin>Payments</Heading.H3>
								</Section.Header>
								<Section.Body>
									<Table
										columns={anyPaymentsInLocalCurrency ? paymentTableColumnsWithExhange : paymentTableColumns}
										columnWidths={paymentTableColumnWidths}
										className={styles["items-table"]}
									>
										<Table.Header className={styles["items-table__header"]}>
											<Table.Row>
												<Table.Heading column="index" noResize>
													#
												</Table.Heading>
												<Table.Heading column="issueDate" noResize>
													Date
												</Table.Heading>
												<Table.Heading column="paymentMethod" noResize>
													Payment Method
												</Table.Heading>
												<Table.Heading column="amount" noResize className={styles["items-table__amount-column"]}>
													Amount
												</Table.Heading>
												<If condition={anyPaymentsInLocalCurrency}>
													<Table.Heading column="exchange" noResize></Table.Heading>
												</If>
											</Table.Row>
										</Table.Header>
										<Table.Body>
											<Conditional>
												<If condition={paymentLineItems.length > 0}>
													{paymentLineItems.map((lineItem, i) => (
														<Table.Row key={lineItem.id} className={styles["items-table__row"]}>
															<Table.Cell column="index" numeric>
																{i + 1}
															</Table.Cell>
															<Table.Cell column="issueDate">
																{PaymentFormat.Fields.issueDate(lineItem.payment.issueDate)}
															</Table.Cell>
															<Table.Cell column="paymentMethod">
																{PaymentFormat.Fields.paymentMethod(lineItem.payment.paymentMethod)}
															</Table.Cell>
															<Table.Cell column="amount" className={styles["items-table__amount-column"]}>
																{PaymentLineItemFormat.Fields.amount(lineItem.amount)}
															</Table.Cell>
															<If condition={anyPaymentsInLocalCurrency}>
																<Table.Cell column="exchange" style={{ overflow: "visible" }}>
																	<If condition={lineItem.payment.isInLocalCurrency}>
																		<Tooltip.Container>
																			<Tooltip className="tooltip--inverted">
																				<Tooltip.Body>
																					<span className="nowrap">
																						Exchanged from: {PaymentLineItemFormat.amount(lineItem)}
																					</span>
																				</Tooltip.Body>
																			</Tooltip>

																			<Icon.Swap size={16} color="var(--primary-color)" />
																		</Tooltip.Container>
																	</If>
																</Table.Cell>
															</If>
														</Table.Row>
													))}
												</If>
												<Else>
													<Table.Row className={styles["items-table__row"]}>
														<Table.Cell
															column={paymentTableColumns[0]}
															colspan={paymentTableColumns.length}
															style={{ color: "var(--neutral-colors--tertiary)" }}
														>
															No payments made yet.
														</Table.Cell>
													</Table.Row>
												</Else>
											</Conditional>
											<If condition={variant === "admin" && amountDue > 0}>
												<Table.Row className={styles["items-table__row"]}>
													<Table.Cell column={paymentTableColumns[0]} colspan={paymentTableColumns.length}>
														<PaymentCreateModalController
															variant="link"
															size="small"
															studentID={contract.student.id}
															contractID={contract.id}
															formStateArgs={createFormStateArgs}
															onSuccess={onPaymentCreateSuccess}
														>
															<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
																<Icon.Plus size={16} /> Add Payment
															</Row>
														</PaymentCreateModalController>
													</Table.Cell>
												</Table.Row>
											</If>
										</Table.Body>
									</Table>
								</Section.Body>
							</Section>
						</If>
						<If condition={variant === "admin"}>
							<Section>
								<Section.Header>
									<Heading.H3 noMargin>Create Invoice</Heading.H3>
								</Section.Header>
								<Section.Body>
									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<Paragraph>No invoice has been created for this installment yet.</Paragraph>

										<InvoiceCreateOnContractInstallmentButton
											contractInstallmentID={contractInstallmentID}
											variant="link"
										>
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												<Icon.Plus size={16} /> Create Invoice
											</Row>
										</InvoiceCreateOnContractInstallmentButton>
									</Column>
								</Section.Body>
							</Section>
						</If>
					</Conditional>
				</Column>
			</Modal.Body>
			<If condition={variant === "admin" && hasInvoice && amountDue > 0}>
				<Modal.Footer className={styles["modal__footer"]}>
					<Row justify="flex-end">
						<PaymentCreateModalController
							variant="primary"
							studentID={contract.student.id}
							contractID={contract.id}
							formStateArgs={createFormStateArgs}
							onSuccess={onPaymentCreateSuccess}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<Icon.PlusCircle size={20} /> Record Payment
							</Row>
						</PaymentCreateModalController>
					</Row>
				</Modal.Footer>
			</If>
		</React.Fragment>
	);
}

// TODO extract and reuse for other financial model components

type LabeledItemProps = {
	label: string;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

function LabeledItem({ label, className, style, children }: LabeledItemProps) {
	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["labeled-item"], className)}
			style={style}
		>
			<span className={styles["labeled-item__label"]}>{label}</span>
			{children}
		</Column>
	);
}
